/**
 * Tiny formatting helpers shared across widgets. Keep these small and
 * dependency-free — they're called in tight render loops.
 */

const RANK_MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };
export function medal(rank) { return RANK_MEDAL[rank] || `#${rank}`; }

/** Format a gold value with thousands separators + 'g' suffix. */
export function gold(n) {
  const v = Number(n) || 0;
  return v.toLocaleString() + 'g';
}

/** Compact gold for tight spaces: 1.2M / 240k / 9,800. */
export function goldShort(n) {
  const v = Number(n) || 0;
  if (v >= 1e6) return (v / 1e6).toFixed(v >= 1e7 ? 0 : 1) + 'M';
  if (v >= 1e4) return Math.round(v / 1e3) + 'k';
  return v.toLocaleString();
}

export function qty(n) { return Number(n || 0).toLocaleString(); }

/**
 * Per-item gold annotation for stack sales (#88).
 * Returns a parenthesised "(X,XXXg/ea)" string when quant > 1, else ''.
 * Locale-aware grouping via the same gold() formatter used for totals.
 */
export function goldPerEach(price, quant) {
  const q = Number(quant) || 0;
  if (q <= 1) return '';
  const p = Number(price) || 0;
  if (p <= 0) return '';
  const per = Math.round(p / q);
  return '(' + per.toLocaleString() + 'g/ea)';
}

/** Convert a possibly-second epoch to ms. */
export function tsMs(ts) {
  const n = Number(ts) || 0;
  return n < 1e12 ? n * 1000 : n;
}

/** "3m ago" / "2h ago" / "Apr 14" — relative for < 24h, date otherwise. */
export function relTime(ts) {
  if (!ts) return '';
  const ms = tsMs(ts);
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return Math.round(diff / 60_000) + 'm ago';
  if (diff < 86_400_000) return Math.round(diff / 3_600_000) + 'h ago';
  if (diff < 7 * 86_400_000) return Math.round(diff / 86_400_000) + 'd ago';
  const dt = new Date(ms);
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Short ISO date for week pickers etc. */
export function ymd(ts) {
  if (!ts) return '';
  const dt = new Date(tsMs(ts));
  if (isNaN(dt.getTime())) return String(ts);
  return dt.toISOString().slice(0, 10);
}

export function weekRangeLabel(ts) {
  if (!ts) return '';
  const start = new Date(tsMs(ts));
  const end = new Date(start.getTime() + 7 * 86_400_000);
  const opts = { month: 'short', day: 'numeric', timeZone: 'America/New_York' };
  return `${start.toLocaleDateString(undefined, opts)} - ${end.toLocaleDateString(undefined, opts)}`;
}

/** "this week" / "last week" / "3 weeks ago" */
export function weekLabel(off) {
  if (off === 0) return 'this week';
  if (off === 1) return 'last week';
  return `${off} weeks ago`;
}

/** First non-empty field — useful when API field naming drifts. */
export function pick(obj, ...keys) {
  for (const k of keys) {
    if (obj && obj[k] != null && obj[k] !== '') return obj[k];
  }
  return null;
}

/** Quality color (matches v1 palette). Accepts both the colour-named keys
 *  (white/green/blue/purple/gold) and the ESO tier names (normal/fine/
 *  superior/epic/legendary/mythic) so an item name renders the SAME colour
 *  no matter which naming the data uses (#9). */
const QUALITY_COLORS = {
  worn: '#9a9a9a',   trash:     '#9a9a9a',
  white: '#e8e8e8',  normal:    '#e8e8e8',
  green: '#5bffa0',  fine:      '#5bffa0',
  blue: '#7ab8ff',   superior:  '#7ab8ff',
  purple: '#d09cff', epic:      '#d09cff',
  gold: '#ffcc40',   legendary: '#ffcc40',
  orange: '#ff7a3c', mythic:    '#ff7a3c',
};
export function qualityColor(q) { return QUALITY_COLORS[String(q || '').toLowerCase()] || '#aaa'; }

/** Neutral ESO merchant trade sack fallback (replaces Dreugh Wax crafting_outfitter_potion_014). */
export const ESO_ICON_FALLBACK = 'https://esoicons.uesp.net/esoui/art/icons/housing_bre_con_sack004.png';

/** Category-aware ESO icon fallback when an item lacks an explicit icon path. */
export function esoFallbackIconForName(itemName) {
  if (!itemName) return null;
  const n = String(itemName).replace(/\^n/g, '').trim().toLowerCase();
  if (!n) return null;
  if (n.startsWith('blueprint:')) return '/esoui/art/icons/crafting_planfurniture_woodworking_3.png';
  if (n.startsWith('praxis:'))    return '/esoui/art/icons/crafting_planfurniture_enchanting3.png';
  if (n.startsWith('diagram:'))   return '/esoui/art/icons/crafting_planfurniture_blacksmithing3.png';
  if (n.startsWith('pattern:'))   return '/esoui/art/icons/crafting_planfurniture_clothier_3.png';
  if (n.startsWith('design:'))    return '/esoui/art/icons/crafting_planfurniture_provisioning3.png';
  if (n.startsWith('formula:'))   return '/esoui/art/icons/crafting_planfurniture_alchemy3.png';
  if (n.startsWith('sketch:'))    return '/esoui/art/icons/crafting_planfurniture_jewelry_3.png';
  if (n.startsWith('recipe:') || n.includes('treasure map') || n.includes('survey:')) return '/esoui/art/icons/quest_scroll_001.png';
  if (n.startsWith('antiquity lead:')) return '/esoui/art/icons/u30_sealed_scroll.png';
  if (n.startsWith('style page:'))     return '/esoui/art/icons/quest_summerset_completed_report.png';
  if (n.startsWith('crafting motif'))  return n.includes('style') ? '/esoui/art/icons/quest_book_001.png' : '/esoui/art/icons/quest_letter_002.png';
  if (n.includes('writ'))              return '/esoui/art/icons/master_writ_blacksmithing.png';
  if (n.includes('glyph of'))          return '/esoui/art/icons/enchantment_jewelry_reducespellcosts.png';
  if (n.includes('poison'))            return '/esoui/art/icons/crafting_poison_002_red_005.png';
  if (/\b(potion|elixir|draught|essence|sip|philter|tincture|dram)\b/.test(n)) return '/esoui/art/icons/consumable_potion_001_type_005.png';
  return null;
}

/**
 * Normalize an ESO icon path/URL to a usable image src.
 * Handles dds → png, backslashes, missing extension, stale Redfur origins.
 */
export function esoIconUrl(iconPath, itemName) {
  const candidate = (iconPath && typeof iconPath === 'string' && iconPath.trim())
    ? iconPath
    : esoFallbackIconForName(itemName);
  if (!candidate || typeof candidate !== 'string') return ESO_ICON_FALLBACK;
  let p = candidate.trim();
  if (!p) return ESO_ICON_FALLBACK;
  p = p.replace(/\\/g, '/').toLowerCase();
  if (p.endsWith('.dds')) p = p.slice(0, -4) + '.png';
  else if (!p.endsWith('.png') && !p.endsWith('.jpg')) p += '.png';
  if (p.startsWith('http')) {
    return p.replace(/^https?:\/\/[^/]*redfur[^/]*/i, 'https://esoicons.uesp.net');
  }
  if (!p.startsWith('/')) p = '/esoui/art/icons/' + p;
  return encodeURI(`https://esoicons.uesp.net${p}`);
}

/** "12.4/day" frequency label. */
export function freqLabel(f) {
  const v = Number(f) || 0;
  if (!v) return '—';
  return `${v}/day`;
}

/** Mask gemini-flash-tool-retrieval model name as "Fissal's Fido" for dashboard display. */
export function maskModelName(modelName) {
  if (!modelName) return '';
  const str = String(modelName).trim();
  if (!str) return '';
  if (/gemini-flash-tool-retrieval/i.test(str)) {
    return str.replace(/gemini-flash-tool-retrieval/gi, "Fissal's Fido");
  }
  return str;
}
